import React, { useState } from 'react';
import { FiUpload, FiImage } from 'react-icons/fi';
import Swal from 'sweetalert2';
import Layout from '../components/Layout';
import PageHeader from '../components/PageHeader';
import { useSystemImages, useUpdateSystemImage } from '../hooks/useSystemImages';

const KEY_LABELS = {
  home_about_section: 'Home — About Section',
  home_what_we_offer: 'Home — What We Offer',
  about_us_first_section: 'About — First Section',
  about_what_we_offer: 'About — What We Offer',
  companies_what_we_offer: 'Companies — What We Offer',
  students_what_we_offer: 'Students — What We Offer',
};

const getLabel = (key) => KEY_LABELS[key] || key.replace(/_/g, ' ');

const SystemImageCard = ({ item, pendingFile, preview, isUploading, onFileChange, onUpload }) => {
  const displayUrl = preview || item.image_url;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      <div className="p-4 border-b border-gray-200">
        <h3 className="text-sm font-semibold text-gray-900">{getLabel(item.key)}</h3>
        <p className="text-xs text-gray-500 mt-1 font-mono">{item.key}</p>
      </div>

      <div className="aspect-video bg-gray-100 flex items-center justify-center">
        {displayUrl ? (
          <img src={displayUrl} alt={getLabel(item.key)} className="w-full h-full object-contain" />
        ) : (
          <div className="flex flex-col items-center text-gray-400">
            <FiImage className="h-12 w-12 mb-2" />
            <span className="text-sm">No image</span>
          </div>
        )}
      </div>

      <div className="p-4 space-y-3">
        <input
          type="file"
          accept="image/*"
          onChange={(e) => onFileChange(item.id, e.target.files?.[0] || null)}
          disabled={isUploading}
          className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100 disabled:opacity-50"
        />
        {pendingFile && (
          <p className="text-xs text-gray-500">
            Selected: {pendingFile.name} ({(pendingFile.size / 1024).toFixed(1)} KB)
          </p>
        )}
        <button
          type="button"
          onClick={() => onUpload(item)}
          disabled={isUploading || !pendingFile}
          className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-md hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FiUpload className="h-4 w-4" />
          {isUploading ? 'Uploading...' : 'Upload Image'}
        </button>
      </div>
    </div>
  );
}

const SystemImages = ({ user }) => {
  const [pendingFiles, setPendingFiles] = useState({});
  const [previews, setPreviews] = useState({});

  const { data: response, isLoading, error } = useSystemImages();
  const updateMutation = useUpdateSystemImage();

  const images = response?.data || [];

  const handleFileChange = (id, file) => {
    if (!file) {
      setPendingFiles((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      setPreviews((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      return;
    }

    setPendingFiles((prev) => ({ ...prev, [id]: file }));
    setPreviews((prev) => ({ ...prev, [id]: URL.createObjectURL(file) }));
  };

  const handleUpload = async (item) => {
    const file = pendingFiles[item.id];
    if (!file) {
      Swal.fire({
        icon: 'warning',
        title: 'No image selected',
        text: 'Please choose an image file before uploading.',
      });
      return;
    }

    try {
      await updateMutation.mutateAsync({ id: item.id, imageFile: file });
      setPendingFiles((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });
      setPreviews((prev) => {
        const next = { ...prev };
        delete next[item.id];
        return next;
      });

      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Image updated successfully.',
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to update image',
      });
    }
  };

  if (isLoading) {
    return (
      <Layout user={user}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500" />
          <span className="ml-2 text-gray-600">Loading...</span>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout user={user}>
        <div className="space-y-6">
          <PageHeader title="System Images" description="Manage system images for site sections" />
          <div className="bg-red-50 border border-red-200 rounded-md p-4">
            <div className="text-red-800">{error.message}</div>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user}>
      <div className="space-y-0">
        <PageHeader
          title="System Images"
          description="Upload images used across home, about, companies, and students pages"
        />

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mt-6">
          {images.map((item) => (
            <SystemImageCard
              key={item.id}
              item={item}
              pendingFile={pendingFiles[item.id]}
              preview={previews[item.id]}
              isUploading={updateMutation.isPending && updateMutation.variables?.id === item.id}
              onFileChange={handleFileChange}
              onUpload={handleUpload}
            />
          ))}
        </div>

        {images.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-8 text-center text-gray-500 mt-6">
            No system images found.
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SystemImages;
